import { SparkleAIIcon } from 'Components/common/Icons/SparkleAIIcon'
import { AIAssistance } from './component/AIAssistance'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { createReactElement } from 'shared/utils/react.utils'
import { ConversationMessageType, ConversationSenderType } from 'Types/AIAssistance.types'
import { getAIAssistanceResponseApi } from 'HTTPClients/plugins.client'
import { sendAnalyticsTrack } from 'Utils/analytics.utils.ts'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'

export type AIAssistancePluginConfigType = {
    url: string
    llmType: string
    limit: number
    tokenSize: number
}

export class AIAssistancePlugin extends BasePlugin<PluginConfigType<AIAssistancePluginConfigType>> {
    public readonly id = 'aiAssistance'
    private conversation: ConversationMessageType[] = []
    private contextId: string
    private uiUpdater: { forceUpdate: () => void } | null = null

    constructor(
        config: PluginConfigType<AIAssistancePluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
        this.contextId = crypto.randomUUID()
    }

    public getConversationLength(): number {
        return this.conversation.length
    }

    public getContextId = () => {
        return this.contextId
    }

    public getAnalyticsData(): Record<string, unknown> {
        return {
            ai_assistance_used:
                this.conversation.length !== 0
                    ? this.conversation.length
                    : 'ai assistance not enabled',
        }
    }

    public async initialize(studio: Studio) {
        this.studio = studio
        const { element, updater } = createReactElement(AIAssistance, {
            getConversation: this.getConversation,
            onClearChat: this.onClearChat,
            onGenerate: this.onGenerate,
            onNewContext: this.onCreateNewContext,
            onPostMessage: this.onPostMessage,
            getContextId: this.getContextId,
            taskConfig: this.studio?.taskConfig,
            getTaskPrompt: this.getPromptContent,
        })
        studio.workbench.enqueueUiUpdate(accessor => {
            accessor.rightSidebar.addItem({
                element,
                title: 'AI Assistance',
                icon: SparkleAIIcon,
                id: this.id,
            })
        })
        this.uiUpdater = updater
    }

    public reset() {
        this.conversation = []
        this.uiUpdater?.forceUpdate()
    }

    private getPromptContent = () => {
        return this.studio?.taskManager.getPromptContent()
    }

    private getAnalyticsObject = () => {
        const assignmentObject = this.studio!.taskConfig.assignment
        return {
            project_id: assignmentObject.project_id,
            task_id: assignmentObject.task.id,
            user_id: assignmentObject.user_id,
            step_id: assignmentObject.step?.id,
        }
    }

    private getConversation = () => {
        return this.conversation
    }

    private generateUUID = () => {
        this.contextId = crypto.randomUUID()
    }

    private onCreateNewContext = () => {
        this.generateUUID()
        const tempConversations = [...this.conversation]
        tempConversations.push({
            sender: ConversationSenderType.DIVIDER,
            message: 'New conversation',
        })
        this.conversation = [...tempConversations]

        sendAnalyticsTrack('NewChat_Clicked', {
            ...this.getAnalyticsObject(),
            conversation_id: this.contextId,
        })

        this.uiUpdater?.forceUpdate()
    }

    private onClearChat = () => {
        this.generateUUID()
        this.conversation = []

        sendAnalyticsTrack('ClearChat_Clicked', {
            ...this.getAnalyticsObject(),
            conversation_id: this.contextId,
            count_of_messages: this.getConversation().length,
        })

        this.uiUpdater?.forceUpdate()
    }

    private onGenerate = async () => {
        this.generateUUID()
        const tempConversations = [...this.conversation]

        const taskPrompt = this.studio?.taskManager.getPromptContent()
        if (!taskPrompt || typeof taskPrompt.value !== 'string') {
            return
        }
        tempConversations.push({
            sender: ConversationSenderType.DIVIDER,
            message: 'Generating response for the prompt',
        })
        tempConversations.push({
            sender: ConversationSenderType.USER,
            message: taskPrompt.value,
        })

        this.conversation = [...tempConversations]
        this.uiUpdater?.forceUpdate()
        const response = await this.fetchBotResponse(taskPrompt.value)
        this.conversation = [...tempConversations, response]

        sendAnalyticsTrack('CopyPrompt_Clicked', {
            ...this.getAnalyticsObject(),
            conversation_id: this.contextId,
        })

        this.uiUpdater?.forceUpdate()
    }

    private onPostMessage = async (message: string) => {
        const tempConversations = [...this.conversation]
        tempConversations.push({
            sender: ConversationSenderType.USER,
            message,
        })
        this.conversation = tempConversations
        this.uiUpdater?.forceUpdate()
        const response = await this.fetchBotResponse(message)
        this.conversation = [...tempConversations, response]
        this.uiUpdater?.forceUpdate()
    }

    private fetchBotResponse = async (content: string) => {
        const requestPayload = {
            taskId: this.studio!.taskConfig.assignment.task.id,
            taskPluginId: this.config.id,
            contextId: this.contextId,
            content,
        }
        sendAnalyticsTrack('AiAssistance_Response_Init', {
            ...this.getAnalyticsObject(),
            conversation_id: this.contextId,
            count_of_messages: this.conversation.length,
        })

        const startTime = +new Date()
        const aiResp = await getAIAssistanceResponseApi(requestPayload)
        if (aiResp.isOk()) {
            const endTime = +new Date()
            sendAnalyticsTrack('AiAssistance_Response_Success', {
                ...this.getAnalyticsObject(),
                conversation_id: this.contextId,
                count_of_messages: aiResp.value.messages.length,
                model_response_time: endTime - startTime,
            })
            return {
                sender: ConversationSenderType.BOT,
                message: aiResp.value.messages[0].content,
            }
        } else {
            sendAnalyticsTrack('AiAssistance_Response_Failed', {
                ...this.getAnalyticsObject(),
                conversation_id: this.contextId,
                count_of_messages: this.conversation.length,
            })
            return {
                sender: ConversationSenderType.BOT,
                message: 'Failed to get AI response',
            }
        }
    }
}
